/**
 * Plaid API client - calls Gloss API (backend) which proxies to Plaid.
 * All Plaid operations are delegated to the backend.
 */

import { inject, Injectable } from "@angular/core";
import { DataRepositoryService } from "../../DataRepository/data-repository.service";
import { GlossSyncService } from "../gloss.sync.service";
import { getPlaidConfig } from "./plaid.config";
import { ErrorResponse } from "@bitwarden/common/models/response/error.response";
import type {
  GlossPlaidLinkTokenResponse,
  GlossPlaidTokenExchangeResponse,
  GlossPlaidAccountsResponse,
  GlossPlaidBalancesResponse,
  GlossPlaidTransactionsSyncResponse,
  GlossPlaidSyncStatusResponse,
  GlossPlaidItemsResponse,
} from "./plaid.types";

@Injectable({
  providedIn: "root",
})
export class PlaidApi {
  private dataRepositoryService = inject(DataRepositoryService);
  private glossSyncService = inject(GlossSyncService);

  private get baseUrl(): string {
    const { url, apiStage } = getPlaidConfig();
    return apiStage ? `${url}/${apiStage}` : url;
  }

  private withItemId(path: string, itemId?: string): string {
    if (!itemId) {
      return path;
    }
    const sep = path.includes("?") ? "&" : "?";
    return `${path}${sep}item_id=${encodeURIComponent(itemId)}`;
  }

  /**
   * Create a Plaid Link token via Gloss API (backend exchanges credentials with Plaid).
   */
  async createLinkToken(payload: {
    client_user_id?: string;
    item_id?: string;
    account_selection_only?: boolean;
  }): Promise<GlossPlaidLinkTokenResponse> {
    const config = getPlaidConfig();
    const path = config.endpoint.create_token;

    const response = await this.dataRepositoryService.send(
      "POST",
      path,
      payload ?? {},
      true,
      true,
      this.baseUrl,
    );

    return response as GlossPlaidLinkTokenResponse;
  }

  /**
   * Exchange a Plaid public_token for an item via Gloss API.
   */
  async exchangeToken(publicToken: string): Promise<GlossPlaidTokenExchangeResponse> {
    const config = getPlaidConfig();
    const path = config.endpoint.exchange_token;

    const response = await this.dataRepositoryService.send(
      "POST",
      path,
      { public_token: publicToken },
      true,
      true,
      this.baseUrl,
    );

    return response as GlossPlaidTokenExchangeResponse;
  }

  /**
   * Fetch accounts via Gloss API. When itemId provided, scoped to that item.
   */
  async getAccounts(itemId?: string): Promise<GlossPlaidAccountsResponse> {
    const config = getPlaidConfig();
    const path = this.withItemId(config.endpoint.accounts, itemId);

    const response = await this.dataRepositoryService.send(
      "GET",
      path,
      null,
      true,
      true,
      this.baseUrl,
    );

    return response as GlossPlaidAccountsResponse;
  }

  /**
   * Fetch balances via Gloss API. When itemId provided, scoped to that item.
   */
  async getBalances(itemId?: string): Promise<GlossPlaidBalancesResponse> {
    const config = getPlaidConfig();
    const path = this.withItemId(config.endpoint.balances, itemId);

    const response = await this.dataRepositoryService.send(
      "GET",
      path,
      null,
      true,
      true,
      this.baseUrl,
    );

    return response as GlossPlaidBalancesResponse;
  }

  /**
   * Fetch transactions via the Plaid transactions/sync cursor endpoint.
   * When itemId provided, scoped to that item.
   */
  async getTransactions(itemId?: string): Promise<GlossPlaidTransactionsSyncResponse> {
    const config = getPlaidConfig();
    const path = this.withItemId(config.endpoint.transactions_sync, itemId);

    const response = await this.dataRepositoryService.send(
      "GET",
      path,
      null,
      true,
      true,
      this.baseUrl,
    );

    return response as GlossPlaidTransactionsSyncResponse;
  }

  /**
   * Check whether Plaid data is synced (initial/historical updates complete).
   * On generic error: fail-open — returns both flags false.
   * Re-throws null/401/403 so auth flows match other authed calls.
   */
  async getSyncStatus(): Promise<GlossPlaidSyncStatusResponse> {
    const config = getPlaidConfig();
    const path = config.endpoint.sync_status;
    try {
      const response = await this.dataRepositoryService.send(
        "GET",
        path,
        null,
        true,
        true,
        this.baseUrl,
      );

      return response as GlossPlaidSyncStatusResponse;
    } catch (e: unknown) {
      if (e == null) {
        throw e;
      }
      if (e instanceof ErrorResponse && (e.statusCode === 401 || e.statusCode === 403)) {
        throw e;
      }
      await this.glossSyncService.logError(
        [],
        e instanceof Error ? e : new Error(String(e)),
        "Plaid GET sync-status",
        null,
      );
      return { initial_update_complete: false, historical_update_complete: false };
    }
  }

  /**
   * Trigger the initial transaction sync after token exchange, seeding the cursor on the aggregator.
   * @deprecated No longer called from the connect or OAuth flows — the cursor is now seeded lazily
   * by the first getTransactions call. Retain until confirmed unused across all environments, then
   * remove together with the backend endpoint.
   */
  async triggerInitialSync(): Promise<{ cursor_updated: boolean; has_more: boolean }> {
    const config = getPlaidConfig();
    const path = config.endpoint.initial_sync;

    const response = await this.dataRepositoryService.send(
      "POST",
      path,
      {},
      true,
      true,
      this.baseUrl,
    );

    return response as { cursor_updated: boolean; has_more: boolean };
  }

  /**
   * Report that user has saved Plaid data to vault.
   * cursor — the next_cursor from the last fetched page; backend commits it as the new cursor.
   * accountsSaved — pass true after accounts are confirmed saved to vault.
   * itemId — scopes to a specific item when provided.
   */
  async reportImportComplete(opts?: {
    cursor?: string | null;
    accountsSaved?: boolean;
    itemId?: string;
    updateModeCompleted?: boolean;
  }): Promise<{ ok: boolean }> {
    const config = getPlaidConfig();
    const path = this.withItemId(config.endpoint.import_complete, opts?.itemId);

    const payload: Record<string, unknown> = {};
    if (opts?.cursor != null) {
      payload["cursor"] = opts.cursor;
    }
    if (opts?.accountsSaved === true) {
      payload["accounts_saved"] = true;
    }
    if (opts?.updateModeCompleted === true) {
      payload["update_mode_completed"] = true;
    }

    const response = await this.dataRepositoryService.send(
      "POST",
      path,
      payload,
      true,
      true,
      this.baseUrl,
    );

    return response as { ok: boolean };
  }

  /**
   * List the user's linked Plaid items via Gloss API.
   */
  async getItems(): Promise<GlossPlaidItemsResponse> {
    const config = getPlaidConfig();
    const path = config.endpoint.items;

    const response = await this.dataRepositoryService.send(
      "GET",
      path,
      null,
      true,
      true,
      this.baseUrl,
    );

    return response as GlossPlaidItemsResponse;
  }

  /**
   * Remove (unlink) a Plaid item via Gloss API.
   */
  async removeItem(itemId: string): Promise<{ ok: boolean; item_id: string }> {
    const config = getPlaidConfig();
    const path = `${config.endpoint.remove_item}/${encodeURIComponent(itemId)}`;

    const response = await this.dataRepositoryService.send(
      "DELETE",
      path,
      null,
      true,
      true,
      this.baseUrl,
    );

    return response as { ok: boolean; item_id: string };
  }
}
